import { useUpdateCandidateStatus } from "@vidario/api-client";
import { moveBack, type Candidate, type Job } from "@vidario/domain";
import { Button, ConfirmDialog, Textarea, useToast } from "@vidario/ui";
import { Undo2 } from "lucide-react";
import { useState, type ReactElement } from "react";

const MAX_REASON_LENGTH = 1000;

export interface MoveBackToLonglistProps {
  jobId: string;
  candidate: Candidate;
  job: Job;
  isAdmin: boolean;
  /** Where focus goes once the candidate is back on the Longlist. */
  onMoved?: () => void;
  /** Another status change on the page is under way. */
  disabled?: boolean;
  className?: string;
}

/** „Zurück in die Longlist" (#162): a recruiter takes back their own pick from
 * Ausgewählt at once; from the team's stages an admin confirms first, because
 * the candidate may already have been contacted or recommended. */
export function MoveBackToLonglist({
  jobId,
  candidate: c,
  job,
  isAdmin,
  onMoved,
  disabled = false,
  className,
}: MoveBackToLonglistProps): ReactElement | null {
  const update = useUpdateCandidateStatus();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const rule = moveBack(c.status, { isAdmin });
  if (!rule) return null;

  const move = (): void => {
    update.mutate(
      { jobId, candidateId: c.id, status: "longlist", reason: reason.trim() || null },
      {
        onSuccess: () => {
          setOpen(false);
          setReason("");
          toast.push({
            kind: "success",
            title: "Zurück in der Longlist",
            message: `${c.name} ist wieder in der Longlist.`,
          });
          // After the dialog has closed and handed focus back, or it lands on <body>.
          if (onMoved) window.setTimeout(onMoved, 0);
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const fromRecommendation = c.status === "recommendation";
  // Recruiters read the reason in the history, except for a move out of an
  // unreleased recommendation, which they don't see at all (#162).
  const recruiterReads = !(fromRecommendation && !job.recommendationReleasedAt);
  return (
    <>
      <Button
        variant="tertiary"
        size="sm"
        leftIcon={<Undo2 size={14} />}
        disabled={disabled || update.isPending}
        onClick={() => (rule.confirm ? setOpen(true) : move())}
        {...(className && { className })}
      >
        Zurück in die Longlist
      </Button>
      {rule.confirm && (
        <ConfirmDialog
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setReason("");
          }}
          title={`${c.name} zurück in die Longlist?`}
          confirmLabel="Zurück in die Longlist"
          onConfirm={move}
          loading={update.isPending}
        >
          <p className="m-0 text-sm text-text-secondary">
            {fromRecommendation
              ? `${c.name} wird aus der Empfehlung entfernt.${
                  job.recommendationReleasedAt
                    ? ` Der Recruiter hat ${c.name} bereits in der freigegebenen Empfehlung gesehen.`
                    : ""
                }`
              : `${c.name} wurde möglicherweise schon kontaktiert. Eine schon verschickte Outreach-E-Mail geht nicht noch einmal raus.`}
          </p>
          <div className="mt-4">
            <Textarea
              label="Grund (optional)"
              helper={
                recruiterReads
                  ? "Der Recruiter sieht den Grund im Verlauf. Interne Hinweise gehören in die Vidario-interne Notiz."
                  : "Interne Hinweise gehören in die Vidario-interne Notiz."
              }
              rows={3}
              maxLength={MAX_REASON_LENGTH}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </ConfirmDialog>
      )}
    </>
  );
}
